import { inject, Injectable, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';

import { getPlatformControlModifierState } from '@blaze-shared/shared-util';

import {
  clearHistory,
  requestRedo,
  requestUndo,
  takeHistorySnapshot,
} from './editor-history.actions';
import { getHasFuture, getHasPast, getIsRestoring } from './editor-history.selectors';

@Injectable({ providedIn: 'root' })
export class EditorHistoryFacade {
  private readonly store = inject(Store);
  private readonly zone = inject(NgZone);

  private readonly onKeydownBound = this.onKeydown.bind(this);

  hasFuture$ = this.store.select(getHasFuture);
  hasPast$ = this.store.select(getHasPast);
  isRestoring$ = this.store.select(getIsRestoring);

  requestUndo(): void {
    this.store.dispatch(requestUndo());
  }

  requestRedo(): void {
    this.store.dispatch(requestRedo());
  }

  clearHistory(): void {
    this.store.dispatch(clearHistory());
  }

  takeHistorySnapshot(): void {
    this.store.dispatch(takeHistorySnapshot());
  }

  initializeShortcuts(): void {
    this.zone.runOutsideAngular(() => {
      document.addEventListener('keydown', this.onKeydownBound);
    });
  }

  disposeShortcuts(): void {
    this.zone.runOutsideAngular(() => {
      document.removeEventListener('keydown', this.onKeydownBound);
    });
  }

  private isEditableElement(element: EventTarget | Element | null): boolean {
    if (!element || !(element instanceof HTMLElement)) {
      return false;
    }
    return (
      element.isContentEditable || element.tagName === 'INPUT' || element.tagName === 'TEXTAREA'
    );
  }

  private onKeydown(event: KeyboardEvent): void {
    if (!getPlatformControlModifierState(event)) {
      return;
    }
    if (this.isEditableElement(document.activeElement) || this.isEditableElement(event.target)) {
      return;
    }
    if (event.code !== 'KeyZ') {
      return;
    }

    this.zone.run(() => {
      event.preventDefault();
      if (event.shiftKey) {
        this.requestRedo();
      } else {
        this.requestUndo();
      }
    });
  }
}
